/**
 * UUIDv7 (RFC 9562): a 48-bit big-endian Unix-millisecond timestamp followed by
 * version/variant bits and random data. Unlike `crypto.randomUUID()` (v4, fully
 * random) a v7 id is **time-sortable**, so it doubles as the event idempotency key
 * AND the deterministic tiebreak when two events claim the same `occurred_at`.
 *
 * Layout:
 *   bytes 0-5  : unix_ts_ms (48 bits, big-endian)
 *   byte  6    : version (0b0111) in the high nibble, rand_a high bits in the low
 *   byte  8    : variant (0b10) in the high bits, rand_b in the low
 *   remaining  : random
 *
 * @internal
 */
export function getUuidV7(): string {
    const ts = Date.now()
    const bytes = new Uint8Array(16)

    // 48-bit timestamp, big-endian. Use float division (not bit-shifts): the value
    // exceeds 32 bits, and JS bitwise ops are 32-bit and would overflow.
    bytes[0] = Math.floor(ts / 0x10000000000) & 0xff // ts >> 40
    bytes[1] = Math.floor(ts / 0x100000000) & 0xff // ts >> 32
    bytes[2] = Math.floor(ts / 0x1000000) & 0xff // ts >> 24
    bytes[3] = Math.floor(ts / 0x10000) & 0xff // ts >> 16
    bytes[4] = Math.floor(ts / 0x100) & 0xff // ts >> 8
    bytes[5] = ts & 0xff

    // Random fill for bytes 6..15.
    getRandomBytes(bytes.subarray(6))

    // Version 7 in the high nibble of byte 6.
    bytes[6] = (bytes[6] & 0x0f) | 0x70
    // RFC 4122 variant (0b10) in the high bits of byte 8.
    bytes[8] = (bytes[8] & 0x3f) | 0x80

    let out = ''
    for (let i = 0; i < 16; i++) {
        out += bytes[i].toString(16).padStart(2, '0')
        if (i === 3 || i === 5 || i === 7 || i === 9) {
            out += '-'
        }
    }
    return out
}

/**
 * Fills `target` with CSPRNG bytes when available (window + service worker + Node 18+),
 * falling back to Math.random so id generation never throws in a degraded environment.
 *
 * @internal
 */
function getRandomBytes(target: Uint8Array): void {
    const c = (globalThis as any)?.crypto
    if (c && typeof c.getRandomValues === 'function') {
        c.getRandomValues(target)
        return
    }
    for (let i = 0; i < target.length; i++) {
        target[i] = Math.floor(Math.random() * 256)
    }
}
